import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { analytics } from "@/lib/analytics";

/**
 * Mounted once at the App level. Two responsibilities:
 *
 *  1. **Hash-aware scroll restoration on SPA navigation.** Browsers only
 *     auto-scroll to a hash anchor on full-document navigation, not on
 *     React-Router pushState transitions. So `<Link to="/how-it-works#earn">`
 *     would otherwise leave the page scrolled to the top -- this hook reads
 *     `location.hash` after a navigation and scrolls to the matching element
 *     (or `(0, 0)` if no hash is present). It also re-runs when only the hash
 *     changes (same pathname, different anchor) so in-page nav clicks scroll
 *     correctly.
 *
 *  2. **`page_view` analytics on every pathname change.** Hash-only changes
 *     do NOT fire `page_view` -- that matches the GA4 default of one
 *     `page_view` per URL path, with hash updates treated as in-page nav.
 *
 * The component renders nothing.
 */
export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  // Scroll handling. Re-runs on pathname OR hash change.
  //
  // For hash anchors we can't simply scroll on the next frame -- target
  // routes are `React.lazy()` in `App.tsx`, so on a cross-page navigation
  // (e.g. `/` -> `/how-it-works#earn`) the page module is still being
  // fetched + parsed when this effect fires, the target element doesn't
  // exist yet, and a single-frame `getElementById` returns `null`. Falling
  // back to `(0, 0)` in that case dumps the user at the top of the page.
  //
  // The fix is a bounded retry loop: poll for the target element on
  // successive animation frames for up to ~750ms (typical lazy-import
  // resolves in 50-300ms on a warm cache, longer on cold). If it appears,
  // scroll. If the budget runs out, fall back to `(0, 0)` so the user
  // isn't stuck mid-page from a stale scroll position.
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = hash.startsWith("#") ? hash.slice(1) : hash;
    const startedAt = performance.now();
    const MAX_WAIT_MS = 750;

    let rafHandle = 0;
    let cancelled = false;

    const attempt = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (performance.now() - startedAt < MAX_WAIT_MS) {
        rafHandle = requestAnimationFrame(attempt);
      } else {
        window.scrollTo(0, 0);
      }
    };
    rafHandle = requestAnimationFrame(attempt);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafHandle);
    };
  }, [pathname, hash]);

  // Analytics: one `page_view` per pathname, NOT per hash. Hash-only changes
  // are in-page nav -- firing a page_view there would inflate counts and
  // diverge from the GA4 default behavior.
  useEffect(() => {
    analytics.trackEvent({
      name: "page_view",
      properties: { page_path: pathname, page_title: document.title },
    });
  }, [pathname]);

  return null;
};
